import { Content, isFilled, asText } from '@prismicio/client';
import type {
  Client,
  ContentRelationshipField,
  ImageField,
  RichTextField,
} from '@prismicio/client';
import { CaseStudyDocument } from '../../../../prismicio-types';

/**
 * Card-level fields synthesized server-side from the case_hero_highlight
 * slice (company_logo, headline) and the CaseStudiesAbout slice (Facts →
 * Industry). Attached to `doc.data` under names that don't collide with any
 * real case_study field so consumers can read them without inspecting the
 * slice zone — which is stripped before serialization.
 */
export type CardExtras = {
  brand_logo?: ImageField;
  card_headline?: RichTextField;
  /**
   * Industry text. Sourced from the doc's own CaseStudiesAbout Facts group
   * ({label: "Industry", value: "Automotive"}); when the doc is a redesign
   * whose legacy sibling holds the Facts instead (see
   * SUPERSEDED_LEGACY_UIDS_MAP), the legacy's industry is used as fallback.
   */
  card_industry?: string;
};

// A case study is "redesigned" (latest design) when its first slice is
// `case_hero_highlight` — the same marker the detail page and
// scripts/list-redesigned-case-studies.mjs use.
const NEW_DESIGN_FIRST_SLICE = 'case_hero_highlight';

// Slices that may carry the case study's industry, in preference order.
// Editors have spread this field across three legacy slices over time; we
// harvest from all of them so a doc that was authored in any era still
// resolves an industry.
const ABOUT_SLICE_TYPE = 'text_with_highlighted_facts'; // CaseStudiesAbout — Facts group
const CHALLENGE_SLICE_TYPE = 'challenge'; // Challenge — challengeAndSolution.industry
const CASE_STUDY_CHALLENGE_SLICE_TYPE = 'case_study_challenge'; // CaseStudyChallenge — side_bar group
const HERO_CTA_WITH_LOGOS_SLICE_TYPE = 'hero_cta_with_logos'; // HeroCtaWithLogos — withStats/withLogoOnTop.logo (fallback brand logo)

// Legacy UID → redesign UID (keep in sync with the 301s in next.config.mjs).
// Legacy docs are hidden from the grid so a client never shows twice
// (old + rework) while the legacy doc is still live; the redesign wins.
// Some legacy docs still carry richer About Facts than their redesign, so we
// also use this map in reverse to backfill missing industries.
const SUPERSEDED_LEGACY_UIDS_MAP: Record<string, string> = {
  toyota: 'toyota-case-study',
  fox: 'fox-network',
  netflix: 'case-study-netflix',
  'banco-internacional': 'case-study-banco-internacional',
  'latin-american-bank': 'latam-bank',
  sightx: 'sightx-rework',
  'case-study-sightx': 'sightx-rework', // earlier rework, superseded
  deutsch: 'deutsch-la',
};

const SUPERSEDED_LEGACY_UIDS = new Set(Object.keys(SUPERSEDED_LEGACY_UIDS_MAP));

// Reverse index: redesign UID → its legacy sibling UIDs (usually one, but
// sightx-rework has two historical legacies).
const LEGACY_UIDS_BY_REDESIGN: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  for (const [legacy, redesign] of Object.entries(SUPERSEDED_LEGACY_UIDS_MAP)) {
    (map[redesign] ??= []).push(legacy);
  }
  return map;
})();

// Card fields plus the slice zone, so we can read the hero slice's background
// image for redesigned docs and the About slice's Facts for industry.
const FETCH_FIELDS_WITH_SLICES = [
  'case_study.uid',
  'case_study.name',
  'case_study.client',
  'case_study.industry',
  'case_study.image',
  'case_study.description',
  'case_study.slices',
];

/**
 * Extract the industry text from a case study doc, checking every slice
 * where editors have historically stored it (in preference order):
 *   1. CaseStudiesAbout Facts group — {label: "Industry", value: "..."}
 *   2. Challenge (challengeAndSolution variation) — primary.industry text
 *   3. CaseStudyChallenge side_bar group — {category: "Industry", content: RT}
 * Returns the first non-empty match, or undefined.
 */
const extractIndustryFromFacts = (
  doc: CaseStudyDocument,
): string | undefined => {
  const slices = doc.data.slices ?? [];

  // 1. CaseStudiesAbout Facts group
  const facts = (
    slices.find((s) => s.slice_type === ABOUT_SLICE_TYPE) as
      | Content.TextWithHighlightedFactsSlice
      | undefined
  )?.primary?.facts;
  const fromFacts = facts
    ?.find((row) => (row?.label ?? '').trim().toLowerCase() === 'industry')
    ?.value?.trim();
  if (fromFacts) {
    return fromFacts;
  }

  // 2. Challenge slice, challengeAndSolution variation — a plain text field
  const challengeSolution = slices.find(
    (s) =>
      s.slice_type === CHALLENGE_SLICE_TYPE &&
      s.variation === 'challengeAndSolution',
  ) as Content.ChallengeSlice | undefined;
  const fromChallenge = (
    challengeSolution?.primary as { industry?: string } | undefined
  )?.industry?.trim();
  if (fromChallenge) {
    return fromChallenge;
  }

  // 3. CaseStudyChallenge side_bar — {category: "Industry", content: RichText}
  const sideBar = (
    slices.find((s) => s.slice_type === CASE_STUDY_CHALLENGE_SLICE_TYPE) as
      | Content.CaseStudyChallengeSlice
      | undefined
  )?.primary?.side_bar;
  const sidebarIndustryRow = sideBar?.find(
    (row) => (row?.category ?? '').trim().toLowerCase() === 'industry',
  );
  const fromSidebar = sidebarIndustryRow
    ? asText(sidebarIndustryRow.content).trim()
    : '';
  if (fromSidebar) {
    return fromSidebar;
  }

  return undefined;
};

/** Build a `uid → industry` map from any batch of docs that carry Facts. */
const buildIndustryByUid = (docs: CaseStudyDocument[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const doc of docs) {
    const ind = extractIndustryFromFacts(doc);
    if (ind && doc.uid) {
      map.set(doc.uid, ind);
    }
  }
  return map;
};

/**
 * Build the card view of a case study.
 *
 * Redesigned docs author their card content (image, description) inside the
 * `case_hero_highlight` slice, so the hero WINS over the top-level image /
 * description — those are often empty or hold a stray value (e.g. a one-word
 * "SightX"). Brand keeps the top-level name (e.g. "Fox Network") and only falls
 * back to the hero's company name when it's empty.
 *
 * Industry is read from the doc's own CaseStudiesAbout Facts; when missing,
 * it's looked up from any legacy sibling's Facts via
 * `industryByUid` — useful when a redesign hasn't yet had its About slice
 * populated but the legacy version has.
 *
 * The heavy `slices` payload is dropped before serializing to the client.
 */
const toCardDoc = (
  doc: CaseStudyDocument,
  industryByUid: Map<string, string>,
): CaseStudyDocument => {
  // Cast is safe: we match on slice_type first. (The Prismic `Simplify<>`
  // wrapper defeats a `s is ...` type guard, so cast instead.)
  const heroPrimary = (
    doc.data.slices?.find((s) => s.slice_type === NEW_DESIGN_FIRST_SLICE) as
      | Content.CaseHeroHighlightSlice
      | undefined
  )?.primary;

  const ownIndustry = extractIndustryFromFacts(doc);
  const legacyFallbackIndustry = ownIndustry
    ? undefined
    : (LEGACY_UIDS_BY_REDESIGN[doc.uid ?? ''] ?? [])
        .map((uid) => industryByUid.get(uid))
        .find((v): v is string => Boolean(v));

  const data = {
    ...doc.data,
    slices: [] as typeof doc.data.slices,
  } as typeof doc.data & CardExtras;

  const resolvedIndustry = ownIndustry ?? legacyFallbackIndustry;
  if (resolvedIndustry) {
    data.card_industry = resolvedIndustry;
  }

  if (heroPrimary) {
    if (isFilled.image(heroPrimary.background_image)) {
      data.image = heroPrimary.background_image;
    }
    if (isFilled.richText(heroPrimary.description)) {
      data.description = heroPrimary.description;
    }
    const hasBrand = !!data.client?.trim() || !!asText(data.name).trim();
    if (!hasBrand && heroPrimary.company_name) {
      data.client = heroPrimary.company_name;
    }
    if (isFilled.image(heroPrimary.company_logo)) {
      data.brand_logo = heroPrimary.company_logo;
    }
    if (isFilled.richText(heroPrimary.headline)) {
      data.card_headline = heroPrimary.headline;
    }
  }

  // Fallback brand logo: HeroCtaWithLogos slice (withStats / withLogoOnTop
  // variations expose a `logo` Image on primary). Only used when the hero
  // highlight didn't already supply one.
  if (!data.brand_logo) {
    const heroCtaLogo = (
      doc.data.slices?.find(
        (s) => s.slice_type === HERO_CTA_WITH_LOGOS_SLICE_TYPE,
      ) as Content.HeroCtaWithLogosSlice | undefined
    )?.primary as { logo?: ImageField } | undefined;
    if (heroCtaLogo && isFilled.image(heroCtaLogo.logo)) {
      data.brand_logo = heroCtaLogo.logo;
    }
  }

  // Last-resort description fallback: the Challenge slice's
  // challengeAndSolution variation carries a `solution` plain-text field on
  // older docs that lack both a top-level description and a hero highlight.
  // Only runs after every prior source has failed, then wraps the plain text
  // as a single-paragraph RichText so the client renders it uniformly.
  if (!isFilled.richText(data.description)) {
    const challengeSolutionSlice = doc.data.slices?.find(
      (s) =>
        s.slice_type === CHALLENGE_SLICE_TYPE &&
        s.variation === 'challengeAndSolution',
    ) as Content.ChallengeSlice | undefined;
    const solutionText = (
      challengeSolutionSlice?.primary as { solution?: string } | undefined
    )?.solution?.trim();
    if (solutionText) {
      // Cast via unknown: description is typed as RichTextField (an array
      // of RichTextNode subtypes) and TS refuses a direct literal cast, so
      // we route through unknown to build the minimal single-paragraph node
      // we need. Runtime shape matches what PrismicRichText renders.
      data.description = [
        { type: 'paragraph', text: solutionText, spans: [] },
      ] as unknown as typeof data.description;
    }
  }

  return { ...doc, data };
};

/**
 * When the fetched set doesn't already include the legacy siblings of the
 * redesigns being shown (i.e. editor-picks branch), pull just those legacy
 * docs so we can harvest their industry Facts.
 */
const fetchLegacyIndustryMap = async (
  client: Client,
  redesignDocs: CaseStudyDocument[],
): Promise<Map<string, string>> => {
  const legacyUids = new Set<string>();
  for (const doc of redesignDocs) {
    if (extractIndustryFromFacts(doc)) {
      continue;
    } // already has its own
    for (const uid of LEGACY_UIDS_BY_REDESIGN[doc.uid ?? ''] ?? []) {
      legacyUids.add(uid);
    }
  }
  if (legacyUids.size === 0) {
    return new Map();
  }

  const legacies = await Promise.all(
    Array.from(legacyUids).map((uid) =>
      client
        .getByUID<CaseStudyDocument>('case_study', uid, {
          fetch: FETCH_FIELDS_WITH_SLICES,
        })
        .catch(() => undefined),
    ),
  );
  return buildIndustryByUid(
    legacies.filter((d): d is CaseStudyDocument => Boolean(d)),
  );
};

/**
 * Resolve the case study list for any data-driven variation of
 * CaseStudiesCarousel:
 *   1. Editor picks: the `featured_case_studies` Group. Lets an editor curate
 *      / order the grid.
 *   2. Fallback: every case study (redesigned + legacy), minus SUPERSEDED
 *      legacy UIDs so a client never shows twice (old + rework) while the
 *      legacy doc is still live.
 *
 * Each returned doc is mapped through `toCardDoc` so the card can read image /
 * description / brand from the top-level fields regardless of design vintage.
 * Industry is harvested from the About slice, falling back to the legacy
 * sibling's Facts when a redesign hasn't been populated yet.
 *
 * Shared by the `fromMarkedCases` and `caseStudiesFeedTwoColumns` branches of
 * the router; the two variations differ only in presentation.
 */
export const resolveCaseStudiesFromPicks = async (
  client: Client,
  featuredCaseStudies:
    | ReadonlyArray<{ case_study: ContentRelationshipField }>
    | null
    | undefined,
  logLabel: string,
): Promise<CaseStudyDocument[]> => {
  const pickedIds = (featuredCaseStudies ?? [])
    .map((row) => row.case_study)
    .filter(isFilled.contentRelationship)
    .map((rel) => rel.id);

  let caseStudies: CaseStudyDocument[] = [];
  // Industry lookup map is populated below from whichever fetch has the
  // legacy docs on hand. Empty by default = no fallback available.
  let industryByUid = new Map<string, string>();
  let source: 'editor_picks' | 'all' | 'empty' = 'empty';

  if (pickedIds.length > 0) {
    try {
      const fetched = await client.getAllByIDs<CaseStudyDocument>(pickedIds, {
        fetch: FETCH_FIELDS_WITH_SLICES,
      });
      const byId = new Map(fetched.map((doc) => [doc.id, doc]));
      caseStudies = pickedIds
        .map((id) => byId.get(id))
        .filter((doc): doc is CaseStudyDocument => Boolean(doc));
      source = 'editor_picks';
      // Picks won't include legacy siblings — targeted fetch for the
      // ones whose industry we still need.
      industryByUid = await fetchLegacyIndustryMap(client, caseStudies);
    } catch (err) {
      console.warn(
        `${logLabel}: getAllByIDs failed, will fall back to redesigned case studies`,
        err,
      );
    }
  }

  if (caseStudies.length === 0) {
    try {
      const all = await client.getAllByType<CaseStudyDocument>('case_study', {
        fetch: FETCH_FIELDS_WITH_SLICES,
      });
      // Build the industry map from ALL docs (including legacies) before
      // filtering, so redesigns can inherit from their legacy siblings.
      industryByUid = buildIndustryByUid(all);
      caseStudies = all.filter(
        (doc) => !SUPERSEDED_LEGACY_UIDS.has(doc.uid ?? ''),
      );
      if (caseStudies.length > 0) {
        source = 'all';
      }
    } catch (fallbackErr) {
      console.error(`${logLabel}: fallback fetch failed`, fallbackErr);
    }
  }

  const resolved = caseStudies.map((doc) => toCardDoc(doc, industryByUid));
  console.info(
    `${logLabel}: ${resolved.length} case_study docs (source=${source})`,
  );
  return resolved;
};
